import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { addDemandComment } from 'services/demands/demandsServices'
import { errorMessage, successMessage } from 'services/messager'
import styles from './index.module.scss'
import sendIcon from '../../../../assets/icons/send-icon.svg'

const COMMENT_CHAR_LIMIT = 1000

const colorsMap = [
	'#F03B3B',
	'#FF9900',
	'#009933',
	'#007AFF',
	'#FFD700',
	'#0000FF',
	'#FFBFBD',
	'#B237F9',
	'#FF0074',
]

function CommentsSection({ comments = [], taskId, canAddComment = false }) {
	const user = useSelector(({ session }) => session)
	const [commentText, setCommentText] = useState('')
	const queryClient = useQueryClient()

	const { mutate: handleAddComment, isPending: isLoading } = useMutation({
		mutationFn: async () => {
			if (!commentText.trim()) return
			console.log(taskId)
			return await addDemandComment({ taskId, comment: commentText })
		},
		onSuccess: () => {
			setCommentText('')
			queryClient.invalidateQueries({ queryKey: ['demand-workflow', taskId] })
			successMessage({ content: 'Comentário adicionado com sucesso!' })
		},
		onError: (error) => {
			console.error(error)
			errorMessage({ content: 'Erro ao adicionar comentário.' })
		},
	})

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleAddComment()
		}
	}

	function getUserInitials(name) {
		if (!name) return '??'
		const initials = name
			.split(' ')
			.map((n) => n[0])
			.join('')
			.replace(/[^a-zA-Z]/g, '')
		if (initials.length > 2) {
			return initials.slice(0, 2).toUpperCase()
		}
		return initials.toUpperCase()
	}

	return (
		<section className={styles.commentsSection}>
			<h2 className={styles.commentsTitle}>
				Comentários
				<span className={styles.commentsCount}>
					({comments?.length.toString().padStart(2, '0')})
				</span>
			</h2>

			{canAddComment && (
				<div className={styles.commentInputWrapper}>
					<div
						className={styles.avatarPlaceholder}
						style={{ backgroundColor: colorsMap[user.id % colorsMap.length] }}>
						<span className={styles.avatarInitials}>
							{getUserInitials(user.name)}
						</span>
					</div>

					<div className={styles.textareaWrapper}>
						<textarea
							className={styles.commentTextarea}
							placeholder="Digite seu comentário aqui"
							rows={3}
							value={commentText}
							onChange={(e) => setCommentText(e.target.value)}
							onKeyDown={handleKeyDown}
							disabled={isLoading}
							maxLength={COMMENT_CHAR_LIMIT}
						/>
						<span className={styles.charCounter} aria-live="polite">
							{commentText.length}/{COMMENT_CHAR_LIMIT}
						</span>
						<button
							type="button"
							className={`${styles.sendButton} ${isLoading || !commentText.trim() ? styles.sendButtonDisabled : ''}`}
							aria-label="Enviar comentário"
							onClick={() => handleAddComment()}
							disabled={isLoading || !commentText.trim()}>
							<img src={sendIcon} fill="currentColor" width={16} height={16} />
						</button>
					</div>
				</div>
			)}

			<div className={styles.commentsList}>
				{comments.map((comment, index) => (
					<div key={comment.id || index} className={styles.commentItem}>
						<div
							className={styles.avatarPlaceholder}
							style={{
								backgroundColor:
									colorsMap[comment.author?.id % colorsMap.length],
							}}>
							<span className={styles.avatarInitials}>
								{getUserInitials(comment.author?.name)}
							</span>
						</div>

						<div className={styles.commentContent}>
							<div className={styles.commentMeta}>
								<strong className={styles.commentAuthor}>
									{comment.author?.name || 'Usuário'}
								</strong>
								<span className={styles.commentDate}>
									{comment.author?.team?.name
										? `${comment.author.team.name} |`
										: 'Solicitante'}
								</span>
								<span className={styles.commentDate}>
									{new Date(
										comment.createdAt || comment.created_at || Date.now()
									).toLocaleString('pt-BR', {
										day: '2-digit',
										month: '2-digit',
										year: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									}) || 'Agora'}
								</span>
							</div>
							<p className={styles.commentText}>
								{comment.comment || comment.content || comment.text}
							</p>
						</div>
					</div>
				))}
			</div>
		</section>
	)
}

export default CommentsSection
